/** Builder → Try it and Tests; promotion and case entry points (ADR-0062). */
export default {
  en: {
    tab: { try: "Try it", tests: "Tests" },
    cancel: "Cancel",
    env: { dev: "development", test: "testing", staging: "final check", prod: "Live" },
    lang: { ar: "Arabic", en: "English" },
    status: { idle: "Not started", running: "Working…", awaiting_approval: "Waiting for approval", awaiting_input: "Waiting for your answer", finished: "Done", error: "Stopped with a problem", cancelled: "Stopped" },
    conv: {
      sending: "Sending…",
      remembersNone: "A new conversation: the agent sees only this question.",
      newConversation: "New conversation",
      case: "Run inside a case",
      noCase: "No case",
      noCases: "No cases you can open",
      caseHelp: "The try is recorded on the case, as when the agent works on it: its approvals and result appear in the case's timeline. The agent does not read the case's details unless one of its steps looks them up. Leave empty to test without a case.",
      langAuto: "Automatic",
      passes: "Each try sends your question, its language (automatic unless you choose one), the earlier turns of this conversation and the documents you attach, and is recorded on the case you choose. Nothing else: no memory of other conversations.",
    },
    picker: {
      agent: "Agent",
      version: "Version",
      choose: "Choose an agent",
      released: "Version {{version}} · running in {{envs}}",
      draft: "Version {{version}} · draft, saved {{at}}",
      submitted: "Version {{version}} · ready to release",
      and: " and ",
    },
    release: {
      reason: "Released from the builder to try and test it",
      done: "{{agent}} version {{version}} now runs in Development. Try it again.",
      waiting: "{{agent}} version {{version}} is waiting to be released to Development.",
      failed: "The release did not work: {{message}}",
      button: "Release version {{version}} to Development",
      submitAndRelease: "Save version {{version}} and release it to Development",
      draftNote: "Submitting makes this version read-only. Keep editing in a new version afterwards.",
    },
    problem: {
      not_in_groups: { title: "This agent is only for some groups", text: "You are not in the groups that may use this agent. Ask its owner to add one of your groups under Builder › Steps › About this agent, or try another agent." },
      reasons: "Details: {{reasons}}",
      not_released: { title: "Release it to development first", text: "An agent is tried and tested in the Development stage. Release a version there once — it needs no approval and takes a moment. After that you can try any draft." },
      no_identity: { title: "This agent has no identity yet", text: "Agents act with their own identity. Release the agent to development again, or ask an agent administrator to create its identity on its card under Governance › Agents." },
      token_refresh_needed: { title: "This agent was just released", text: "Your sign-in is from before the release. Reload the page and try again." },
      not_dev: { title: "Testing is only available in development", text: "This stage runs real work. Try it and Tests work in the Development stage only." },
      forbidden: { title: "You cannot test this agent", text: "Trying and testing agents needs the builder or agent administrator role in this organisation." },
      policy: { title: "The platform's rules stopped this test", text: "A rule did not allow this test to start." },
      not_found: { title: "This version was not found", text: "It may have been removed. Choose another version." },
      no_secret: { title: "Create the secret first", text: "Create the secret for this agent, then send a test event." },
      no_cases: { title: "There are no tests yet", text: "Add at least one test, then run them." },
      too_many_cases: { title: "Too many tests for one run", text: "A run can hold up to 50 tests. Remove some and try again." },
      other: { title: "Something went wrong" },
    },
    try: {
      intro: "Ask the agent something and watch what it does, step by step. This uses the version you choose, including drafts you have saved in the editor.",
      neverReleased: "This agent has never been released to development. Release a version once to start trying it.",
      tierTitle: "This version needs risk level {{tier}}",
      tierText: "Tests run with the risk level of the version in Development ({{version}}, level {{released}}), so this version's systems would be refused. Release this version to Development first.",
      safe: "Safe test",
      safeText: "Look-ups run for real, so you see real data and real failures. Only actions that would change something are simulated: nothing is sent, booked, paid or changed. Steps that would need an approval are skipped and shown to you. When the AI is not sure of a decision, it goes on with its own suggestion instead of asking a person. Questions for a person are asked here.",
      realSwitch: "Let it really do things (sends real e-mails, changes records)",
      realConfirmTitle: "Let the agent really do things?",
      realConfirmText: "The agent may send real messages and change real records in connected systems. Approvals will be asked for as usual. Use this only with test data.",
      realConfirmOk: "Yes, allow real actions",
      realOn: "Real actions are on",
      realOnText: "The agent can really send and change things in this test. Approvals go to the reviewers as usual.",
      conversation: "Conversation",
      empty: "Type a question below and choose Try it.",
      role: { user: "You", assistant: "Agent" },
      skippedApproval: "Approval would be asked here (skipped in a safe test)",
      skippedRoles: "In real use, a person with the role {{roles}} approves this step.",
      failed: "The test stopped with a problem",
      finishedSafe: "Done. Nothing was really changed.",
      finishedReal: "Done.",
      finishedProblems: "Finished, but some steps did not work (see the conversation).",
      ask: "Ask it something",
      placeholder: "For example: What is the status of invoice INV-1042?",
      language: "Language of the question",
      send: "Try it",
      stop: "Stop",
      makeTest: "Turn this into a test",
      makeTestHelp: "Keep this question and answer as a test you can run before every release.",
      activity: "What it is doing",
      simulated: "Simulated actions",
      real: "Real actions",
      toolsSimulated: "Look-ups below ran for real. Actions that would change something were simulated: the systems were asked what would happen, nothing changed.",
      sources: "Sources it used",
    },
    tests: {
      intro: "Tests are questions with what a good answer looks like. Run them before every release: the platform asks the agent each question in development and checks the answers for you.",
    },
    checks: {
      facts_one: "Mentions {{count}} fact",
      facts_other: "Mentions {{count}} facts",
      notSay_one: "Avoids {{count}} phrase",
      notSay_other: "Avoids {{count}} phrases",
      tools_one: "Uses {{count}} tool",
      tools_other: "Uses {{count}} tools",
      forbidden_one: "Never does {{count}} thing",
      forbidden_other: "Never does {{count}} things",
      refuse: "Politely refuses",
      sources: "Sticks to the reference text",
      answers: "Answers without errors",
    },
    cases: {
      title_one: "{{count}} test",
      title_other: "{{count}} tests",
      add: "Add a test",
      runAll: "Run all tests on {{version}}",
      loadFailed: "The tests could not be loaded",
      empty: "No tests yet. Add a question and what a good answer looks like.",
      question: "Question",
      language: "Language",
      checks: "What it checks",
      actions: "Actions",
      edit: "Edit test: {{q}}",
      remove: "Remove",
      removeConfirm: "Remove this test?",
      removeAria: "Remove test: {{q}}",
      added: "Test added",
      updated: "Test saved",
      removed: "Test removed",
    },
    form: {
      addTitle: "Add a test",
      editTitle: "Edit the test",
      save: "Save test",
      failed: "The test could not be saved",
      from: { case: "Started from a case. Check the question and the answer before saving.", feedback: "Started from reviewer feedback. Check it before saving." },
      question: "Question",
      questionHelp: "Write it the way a real person would ask.",
      language: "Language",
      title: "Short name (optional)",
      titleHelp: "Helps you find the test in the list.",
      goodAnswer: "What a good answer looks like",
      expected: "A good answer",
      expectedHelp: "Shown next to the agent's answer so you can compare them.",
      facts: "Must mention",
      factsHelp: "Words or numbers that must appear, for example an invoice number or an amount. Press Enter after each one.",
      notSay: "Must not say",
      notSayHelp: "Anything that must never appear in the answer. Press Enter after each one.",
      tagsPlaceholder: "Type and press Enter",
      tools: "Should use",
      toolsHelp: "Systems the agent should use to answer.",
      forbidden: "Must never do",
      forbiddenHelp: "Actions the agent must never take for this question.",
      refuse: "The agent should politely refuse",
      refuseHelp: "Turn on when the right answer is to say no, for example a request outside its job.",
      reference: "Reference text (optional)",
      referenceHelp: "Paste the policy or document the answer must be based on. Then the platform also checks that the agent sticks to it.",
    },
    history: {
      title: "Earlier runs",
      empty: "The tests have not been run yet.",
      line: "{{at}} · version {{version}} · by {{by}}",
      open: "Show",
      openAria: "Show the results from {{at}}",
    },
    results: {
      title: "Results for version {{version}} · {{at}}",
      running: "Running",
      couldNotFinish: "Could not finish",
      passedTag: "Passed",
      failedTag: "Needs work",
      progress: "Asking the agent… {{done}} of {{total}} done",
      progressAria: "Progress of the test run",
      summary: "{{passed}} of {{total}} passed",
      errored_one: "{{count}} could not run",
      errored_other: "{{count}} could not run",
      safeNote: "Tests run as safe tests: nothing was really sent or changed.",
      cases: "Each test",
      status: { pending: "Waiting", passed: "Passed", failed: "Failed", error: "Could not run" },
      question: "Question:",
      why: "Why it failed",
      expected: "A good answer",
      noExpected: "No answer was written for this test.",
      mustMention: "Must mention:",
      got: "The agent's answer",
      waiting: "Waiting for the agent…",
      noAnswer: "The agent gave no answer.",
      usedTools: "Used:",
      took: "Took {{d}}",
      cost: "cost {{c}}",
    },
    gates: {
      title: "Release goals",
      metric: {
        task_success: "Answers correctly",
        groundedness: "Sticks to its sources",
        safety: "Stays safe",
        policy_violations: "Rules broken",
        tool_call_accuracy: "Uses the right tools",
        arabic_quality: "Writes good Arabic",
        latency_p95_ms: "Slowest answers take",
        cost_per_case_usd: "Cost per question",
      },
      line: "{{name}} {{value}} — goal {{goal}} — {{status}}",
      atLeast: "at least {{v}}",
      atMost: "at most {{v}}",
      status: { pass: "passed", fail: "not yet", not_measured: "not checked" },
      notMeasured: "{{name}} — not checked: {{how}}",
      how: {
        groundedness: "add reference text to a test to check this",
        tool_call_accuracy: "add “Should use” to a test to check this",
        arabic_quality: "add an Arabic test to check this",
        default: "no test covers this yet",
      },
    },
    post: {
      counted: "These results now count for releases.",
      countedFailed: "These results count for releases, but the release goals are not met yet.",
      unblocked_one: "{{count}} waiting release can now move on to approval.",
      unblocked_other: "{{count}} waiting releases can now move on to approval.",
      skipped: {
        draft: "Only results for a submitted version count for releases. This was a draft.",
        too_few_cases: "Add at least 5 tests so the results count for releases.",
        not_finished: "The results count for releases once every test has finished.",
        post_failed: "The results could not be sent to the release check. Run the tests again.",
      },
    },
    reason: {
      missing_fact: "It did not mention “{{detail}}”.",
      said_forbidden: "It said “{{detail}}”, which it must not say.",
      missing_tool: "It did not use {{detail}}.",
      used_forbidden_tool: "It tried to {{detail}}, which it must never do.",
      should_refuse: "It should have politely refused.",
      should_not_refuse: "It refused, but it should have answered.",
      leaked_private_data: "It showed private data that was not in the question.",
      not_grounded: "Its answer is not based on the reference text.",
      run_failed: "The agent stopped with a problem: {{detail}}",
      waiting_for_person: "It waited for a person to answer, so the test could not finish.",
      timeout: "It took too long to answer.",
      start_failed: "The test could not start: {{detail}}",
    },
    fromCase: { button: "Turn into a test" },
    promotion: {
      runTests: "Run the tests",
      runTestsAria: "Run the tests of {{agent}}",
      retry: "Retry deployment",
      retryAria: "Retry the deployment of {{agent}}",
      retried: "{{agent}}: {{status}}",
      retryFailed: "The deployment failed again: {{message}}",
    },
  },
  ar: {
    tab: { try: "جرّبه", tests: "الاختبارات" },
    cancel: "إلغاء",
    env: { dev: "التطوير", test: "الاختبار", staging: "الفحص النهائي", prod: "التشغيل الفعلي" },
    lang: { ar: "العربية", en: "الإنجليزية" },
    status: { idle: "لم يبدأ", running: "يعمل…", awaiting_approval: "بانتظار الاعتماد", awaiting_input: "بانتظار إجابتك", finished: "انتهى", error: "توقف بسبب مشكلة", cancelled: "أُوقف" },
    conv: {
      sending: "جارٍ الإرسال…",
      remembersNone: "محادثة جديدة: يرى الوكيل هذا السؤال فقط.",
      newConversation: "محادثة جديدة",
      case: "شغّله ضمن حالة",
      noCase: "بلا حالة",
      noCases: "لا توجد حالات يمكنك فتحها",
      caseHelp: "تُسجَّل التجربة على الحالة كما لو كان الوكيل يعمل عليها: تظهر اعتماداتها ونتيجتها في سجل الحالة. لا يقرأ الوكيل تفاصيل الحالة إلا إذا بحثت عنها إحدى خطواته. اتركه فارغًا للاختبار بلا حالة.",
      langAuto: "تلقائي",
      passes: "ترسل كل تجربة سؤالك ولغته (تلقائية ما لم تخترها) والأدوار السابقة في هذه المحادثة والمستندات التي ترفقها، وتُسجَّل على الحالة التي تختارها. لا شيء غير ذلك: لا ذاكرة لمحادثات أخرى.",
    },
    picker: {
      agent: "الوكيل",
      version: "الإصدار",
      choose: "اختر وكيلًا",
      released: "الإصدار {{version}} · يعمل في {{envs}}",
      draft: "الإصدار {{version}} · مسودة، حُفظت {{at}}",
      submitted: "الإصدار {{version}} · جاهز للإطلاق",
      and: " و",
    },
    release: {
      reason: "أُطلق من منشئ الوكلاء لتجربته واختباره",
      done: "يعمل {{agent}} بالإصدار {{version}} الآن في مرحلة التطوير. جرّبه مرة أخرى.",
      waiting: "ينتظر {{agent}} بالإصدار {{version}} إطلاقه إلى مرحلة التطوير.",
      failed: "لم ينجح الإطلاق: {{message}}",
      button: "أطلق الإصدار {{version}} إلى مرحلة التطوير",
      submitAndRelease: "احفظ الإصدار {{version}} وأطلقه إلى مرحلة التطوير",
      draftNote: "التقديم يجعل هذا الإصدار للقراءة فقط. تابع التعديل في إصدار جديد بعد ذلك.",
    },
    problem: {
      not_in_groups: { title: "هذا الوكيل لمجموعات محددة فقط", text: "لست ضمن المجموعات المسموح لها باستخدام هذا الوكيل. اطلب من مالكه إضافة إحدى مجموعاتك من المنشئ › الخطوات › عن هذا الوكيل، أو جرّب وكيلًا آخر." },
      reasons: "التفاصيل: {{reasons}}",
      not_released: { title: "أطلقه إلى مرحلة التطوير أولًا", text: "تتم تجربة الوكيل واختباره في مرحلة التطوير. أطلق إصدارًا هناك مرة واحدة — لا يحتاج إلى اعتماد ويستغرق لحظة. بعدها يمكنك تجربة أي مسودة." },
      no_identity: { title: "ليست لهذا الوكيل هوية بعد", text: "يعمل الوكلاء بهويتهم الخاصة. أطلق الوكيل إلى مرحلة التطوير مرة أخرى، أو اطلب من مسؤول الوكلاء إنشاء هويته من بطاقته في الحوكمة › الوكلاء." },
      token_refresh_needed: { title: "أُطلق هذا الوكيل للتو", text: "تسجيل دخولك سابق للإطلاق. أعد تحميل الصفحة وحاول مرة أخرى." },
      not_dev: { title: "الاختبار متاح في مرحلة التطوير فقط", text: "هذه المرحلة تنفذ عملًا حقيقيًا. التجربة والاختبارات متاحة في مرحلة التطوير فقط." },
      forbidden: { title: "لا يمكنك اختبار هذا الوكيل", text: "تجربة الوكلاء واختبارهم تتطلب دور منشئ الوكلاء أو مسؤول الوكلاء في هذه المؤسسة." },
      policy: { title: "أوقفت قواعد المنصة هذا الاختبار", text: "لم تسمح إحدى القواعد ببدء هذا الاختبار." },
      not_found: { title: "لم يُعثر على هذا الإصدار", text: "ربما حُذف. اختر إصدارًا آخر." },
      no_secret: { title: "أنشئ المفتاح السري أولًا", text: "أنشئ المفتاح السري لهذا الوكيل، ثم أرسل حدثًا تجريبيًا." },
      no_cases: { title: "لا توجد اختبارات بعد", text: "أضف اختبارًا واحدًا على الأقل، ثم شغّل الاختبارات." },
      too_many_cases: { title: "اختبارات كثيرة جدًا لتشغيل واحد", text: "يتسع التشغيل الواحد لخمسين اختبارًا كحد أقصى. احذف بعضها وحاول مرة أخرى." },
      other: { title: "حدث خطأ ما" },
    },
    try: {
      intro: "اسأل الوكيل شيئًا وتابع ما يفعله خطوة بخطوة. يُستخدم الإصدار الذي تختاره، بما في ذلك المسودات التي حفظتها في المحرر.",
      neverReleased: "لم يُطلق هذا الوكيل إلى مرحلة التطوير من قبل. أطلق إصدارًا مرة واحدة لتبدأ تجربته.",
      tierTitle: "يحتاج هذا الإصدار إلى مستوى الخطورة {{tier}}",
      tierText: "تعمل الاختبارات بمستوى خطورة الإصدار الموجود في مرحلة التطوير ({{version}}، المستوى {{released}})، لذا ستُرفض أنظمة هذا الإصدار. أطلق هذا الإصدار إلى مرحلة التطوير أولًا.",
      safe: "اختبار آمن",
      safeText: "عمليات الاستعلام تُنفَّذ فعليًا، فترى بيانات حقيقية وأخطاء حقيقية. أما الإجراءات التي تغيّر شيئًا فمحاكاة فقط: لا يُرسل شيء ولا يُحجز ولا يُدفع ولا يتغير. الخطوات التي تحتاج إلى اعتماد تُتخطى وتُعرض لك. وعندما لا يكون الذكاء الاصطناعي متأكدًا من قرار، يتابع باقتراحه بدل سؤال شخص. أما الأسئلة الموجّهة لشخص فتُطرح هنا.",
      realSwitch: "اسمح له بتنفيذ الإجراءات فعلًا (يرسل رسائل حقيقية ويغيّر السجلات)",
      realConfirmTitle: "هل تسمح للوكيل بتنفيذ الإجراءات فعلًا؟",
      realConfirmText: "قد يرسل الوكيل رسائل حقيقية ويغيّر سجلات حقيقية في الأنظمة المتصلة. ستُطلب الاعتمادات كالمعتاد. استخدم هذا مع بيانات تجريبية فقط.",
      realConfirmOk: "نعم، اسمح بالإجراءات الحقيقية",
      realOn: "الإجراءات الحقيقية مفعّلة",
      realOnText: "يمكن للوكيل في هذا الاختبار أن يرسل ويغيّر الأشياء فعلًا. تذهب الاعتمادات إلى المراجعين كالمعتاد.",
      conversation: "المحادثة",
      empty: "اكتب سؤالًا في الأسفل واختر «جرّبه».",
      role: { user: "أنت", assistant: "الوكيل" },
      skippedApproval: "كان سيُطلب اعتماد هنا (تم تخطيه في الاختبار الآمن)",
      skippedRoles: "في الاستخدام الفعلي يعتمد هذه الخطوة شخص لديه الدور {{roles}}.",
      failed: "توقف الاختبار بسبب مشكلة",
      finishedSafe: "انتهى. لم يتغير شيء فعليًا.",
      finishedReal: "انتهى.",
      finishedProblems: "انتهى، لكن بعض الخطوات لم تنجح (انظر المحادثة).",
      ask: "اسأله شيئًا",
      placeholder: "مثلًا: ما حالة الفاتورة INV-1042؟",
      language: "لغة السؤال",
      send: "جرّبه",
      stop: "إيقاف",
      makeTest: "حوّل هذا إلى اختبار",
      makeTestHelp: "احتفظ بهذا السؤال والإجابة كاختبار تشغّله قبل كل إطلاق.",
      activity: "ما يفعله الآن",
      simulated: "إجراءات محاكاة",
      real: "إجراءات حقيقية",
      toolsSimulated: "عمليات الاستعلام أدناه نُفّذت فعليًا. أما الإجراءات التي تغيّر شيئًا فكانت محاكاة: سُئلت الأنظمة عما سيحدث، ولم يتغير شيء.",
      sources: "المصادر التي استخدمها",
    },
    tests: {
      intro: "الاختبارات أسئلة مع وصف للإجابة الجيدة. شغّلها قبل كل إطلاق: تسأل المنصة الوكيل كل سؤال في مرحلة التطوير وتتحقق من الإجابات نيابة عنك.",
    },
    checks: {
      facts_zero: "يذكر {{count}} معلومة",
      facts_one: "يذكر معلومة واحدة",
      facts_two: "يذكر معلومتين",
      facts_few: "يذكر {{count}} معلومات",
      facts_many: "يذكر {{count}} معلومة",
      facts_other: "يذكر {{count}} معلومة",
      notSay_zero: "يتجنب {{count}} عبارة",
      notSay_one: "يتجنب عبارة واحدة",
      notSay_two: "يتجنب عبارتين",
      notSay_few: "يتجنب {{count}} عبارات",
      notSay_many: "يتجنب {{count}} عبارة",
      notSay_other: "يتجنب {{count}} عبارة",
      tools_zero: "يستخدم {{count}} أداة",
      tools_one: "يستخدم أداة واحدة",
      tools_two: "يستخدم أداتين",
      tools_few: "يستخدم {{count}} أدوات",
      tools_many: "يستخدم {{count}} أداة",
      tools_other: "يستخدم {{count}} أداة",
      forbidden_zero: "لا يفعل {{count}} أشياء أبدًا",
      forbidden_one: "لا يفعل شيئًا واحدًا أبدًا",
      forbidden_two: "لا يفعل شيئين أبدًا",
      forbidden_few: "لا يفعل {{count}} أشياء أبدًا",
      forbidden_many: "لا يفعل {{count}} شيئًا أبدًا",
      forbidden_other: "لا يفعل {{count}} شيء أبدًا",
      refuse: "يعتذر بلطف",
      sources: "يلتزم بالنص المرجعي",
      answers: "يجيب دون أخطاء",
    },
    cases: {
      title_zero: "لا توجد اختبارات",
      title_one: "اختبار واحد",
      title_two: "اختباران",
      title_few: "{{count}} اختبارات",
      title_many: "{{count}} اختبارًا",
      title_other: "{{count}} اختبار",
      add: "أضف اختبارًا",
      runAll: "شغّل كل الاختبارات على {{version}}",
      loadFailed: "تعذّر تحميل الاختبارات",
      empty: "لا توجد اختبارات بعد. أضف سؤالًا ووصفًا للإجابة الجيدة.",
      question: "السؤال",
      language: "اللغة",
      checks: "ما يتحقق منه",
      actions: "الإجراءات",
      edit: "تعديل الاختبار: {{q}}",
      remove: "حذف",
      removeConfirm: "هل تحذف هذا الاختبار؟",
      removeAria: "حذف الاختبار: {{q}}",
      added: "أُضيف الاختبار",
      updated: "حُفظ الاختبار",
      removed: "حُذف الاختبار",
    },
    form: {
      addTitle: "أضف اختبارًا",
      editTitle: "تعديل الاختبار",
      save: "احفظ الاختبار",
      failed: "تعذّر حفظ الاختبار",
      from: { case: "بدأ من حالة. راجع السؤال والإجابة قبل الحفظ.", feedback: "بدأ من ملاحظات مراجع. راجعه قبل الحفظ." },
      question: "السؤال",
      questionHelp: "اكتبه كما يسأله شخص حقيقي.",
      language: "اللغة",
      title: "اسم قصير (اختياري)",
      titleHelp: "يساعدك على إيجاد الاختبار في القائمة.",
      goodAnswer: "كيف تبدو الإجابة الجيدة",
      expected: "إجابة جيدة",
      expectedHelp: "تُعرض بجانب إجابة الوكيل لتقارن بينهما.",
      facts: "يجب أن يذكر",
      factsHelp: "كلمات أو أرقام يجب أن تظهر، مثل رقم فاتورة أو مبلغ. اضغط Enter بعد كل واحدة.",
      notSay: "يجب ألا يقول",
      notSayHelp: "أي شيء يجب ألا يظهر في الإجابة أبدًا. اضغط Enter بعد كل واحدة.",
      tagsPlaceholder: "اكتب واضغط Enter",
      tools: "يجب أن يستخدم",
      toolsHelp: "الأنظمة التي يجب أن يستخدمها الوكيل ليجيب.",
      forbidden: "يجب ألا يفعل أبدًا",
      forbiddenHelp: "إجراءات يجب ألا يتخذها الوكيل أبدًا لهذا السؤال.",
      refuse: "يجب أن يعتذر الوكيل بلطف",
      refuseHelp: "فعّله عندما تكون الإجابة الصحيحة هي الرفض، مثل طلب خارج نطاق عمله.",
      reference: "نص مرجعي (اختياري)",
      referenceHelp: "الصق السياسة أو المستند الذي يجب أن تستند إليه الإجابة. عندها تتحقق المنصة أيضًا من التزام الوكيل به.",
    },
    history: {
      title: "التشغيلات السابقة",
      empty: "لم تُشغّل الاختبارات بعد.",
      line: "{{at}} · الإصدار {{version}} · بواسطة {{by}}",
      open: "اعرض",
      openAria: "اعرض نتائج {{at}}",
    },
    results: {
      title: "نتائج الإصدار {{version}} · {{at}}",
      running: "قيد التشغيل",
      couldNotFinish: "تعذّر الإكمال",
      passedTag: "نجح",
      failedTag: "يحتاج إلى عمل",
      progress: "يسأل الوكيل… اكتمل {{done}} من {{total}}",
      progressAria: "تقدم تشغيل الاختبارات",
      summary: "نجح {{passed}} من {{total}}",
      errored_zero: "لم يتعذر تشغيل أي اختبار",
      errored_one: "تعذّر تشغيل اختبار واحد",
      errored_two: "تعذّر تشغيل اختبارين",
      errored_few: "تعذّر تشغيل {{count}} اختبارات",
      errored_many: "تعذّر تشغيل {{count}} اختبارًا",
      errored_other: "تعذّر تشغيل {{count}} اختبار",
      safeNote: "تعمل الاختبارات كاختبارات آمنة: لم يُرسل أو يتغير شيء فعليًا.",
      cases: "كل اختبار",
      status: { pending: "بالانتظار", passed: "نجح", failed: "فشل", error: "تعذّر تشغيله" },
      question: "السؤال:",
      why: "سبب الفشل",
      expected: "إجابة جيدة",
      noExpected: "لم تُكتب إجابة لهذا الاختبار.",
      mustMention: "يجب أن يذكر:",
      got: "إجابة الوكيل",
      waiting: "بانتظار الوكيل…",
      noAnswer: "لم يقدّم الوكيل إجابة.",
      usedTools: "استخدم:",
      took: "استغرق {{d}}",
      cost: "التكلفة {{c}}",
    },
    gates: {
      title: "أهداف الإطلاق",
      metric: {
        task_success: "يجيب بشكل صحيح",
        groundedness: "يلتزم بمصادره",
        safety: "يبقى آمنًا",
        policy_violations: "القواعد المخالَفة",
        tool_call_accuracy: "يستخدم الأدوات الصحيحة",
        arabic_quality: "يكتب عربية جيدة",
        latency_p95_ms: "أبطأ الإجابات تستغرق",
        cost_per_case_usd: "التكلفة لكل سؤال",
      },
      line: "{{name}} {{value}} — الهدف {{goal}} — {{status}}",
      atLeast: "{{v}} على الأقل",
      atMost: "{{v}} على الأكثر",
      status: { pass: "تحقق", fail: "لم يتحقق بعد", not_measured: "لم يُفحص" },
      notMeasured: "{{name}} — لم يُفحص: {{how}}",
      how: {
        groundedness: "أضف نصًا مرجعيًا إلى اختبار لفحص ذلك",
        tool_call_accuracy: "أضف «يجب أن يستخدم» إلى اختبار لفحص ذلك",
        arabic_quality: "أضف اختبارًا باللغة العربية لفحص ذلك",
        default: "لا يغطي أي اختبار ذلك بعد",
      },
    },
    post: {
      counted: "أصبحت هذه النتائج معتمدة في قرارات الإطلاق.",
      countedFailed: "هذه النتائج معتمدة في قرارات الإطلاق، لكن أهداف الإطلاق لم تتحقق بعد.",
      unblocked_zero: "لا توجد عمليات إطلاق بالانتظار.",
      unblocked_one: "يمكن لعملية إطلاق واحدة بالانتظار أن تنتقل الآن إلى الاعتماد.",
      unblocked_two: "يمكن لعمليتي إطلاق بالانتظار أن تنتقلا الآن إلى الاعتماد.",
      unblocked_few: "يمكن لـ {{count}} عمليات إطلاق بالانتظار أن تنتقل الآن إلى الاعتماد.",
      unblocked_many: "يمكن لـ {{count}} عملية إطلاق بالانتظار أن تنتقل الآن إلى الاعتماد.",
      unblocked_other: "يمكن لـ {{count}} عملية إطلاق بالانتظار أن تنتقل الآن إلى الاعتماد.",
      skipped: {
        draft: "تُعتمد في قرارات الإطلاق نتائج الإصدارات المقدَّمة فقط. هذا كان مسودة.",
        too_few_cases: "أضف 5 اختبارات على الأقل لتُعتمد النتائج في قرارات الإطلاق.",
        not_finished: "تُعتمد النتائج في قرارات الإطلاق بعد انتهاء كل الاختبارات.",
        post_failed: "تعذّر إرسال النتائج إلى فحص الإطلاق. شغّل الاختبارات مرة أخرى.",
      },
    },
    reason: {
      missing_fact: "لم يذكر «{{detail}}».",
      said_forbidden: "قال «{{detail}}»، وهذا ممنوع.",
      missing_tool: "لم يستخدم {{detail}}.",
      used_forbidden_tool: "حاول أن {{detail}}، وهذا ممنوع دائمًا.",
      should_refuse: "كان عليه أن يعتذر بلطف.",
      should_not_refuse: "اعتذر، لكن كان عليه أن يجيب.",
      leaked_private_data: "أظهر بيانات خاصة لم ترد في السؤال.",
      not_grounded: "إجابته لا تستند إلى النص المرجعي.",
      run_failed: "توقف الوكيل بسبب مشكلة: {{detail}}",
      waiting_for_person: "انتظر إجابة من شخص، فلم يكتمل الاختبار.",
      timeout: "استغرق وقتًا طويلًا جدًا للإجابة.",
      start_failed: "تعذّر بدء الاختبار: {{detail}}",
    },
    fromCase: { button: "حوّلها إلى اختبار" },
    promotion: {
      runTests: "شغّل الاختبارات",
      runTestsAria: "شغّل اختبارات {{agent}}",
      retry: "أعد محاولة النشر",
      retryAria: "أعد محاولة نشر {{agent}}",
      retried: "{{agent}}: {{status}}",
      retryFailed: "فشل النشر مرة أخرى: {{message}}",
    },
  },
};
